/** Canonical public Key Feature pages — single source for marketing UI and Feature Document admin. */
export type KeyFeatureCategory = "qapi" | "health" | "operations";

export type KeyFeaturePage = {
  label: string;
  feature: string;
  feature_menu_url: string;
  /** Homepage Key Features card grouping. */
  category: KeyFeatureCategory;
};

export const KEY_FEATURE_PAGES: KeyFeaturePage[] = [
  {
    label: "Committees and Meetings",
    feature: "Committees and Meetings",
    feature_menu_url: "/features/committee-meeting-management",
    category: "qapi",
  },
  {
    label: "Performance Improvement Plans (PIPs)",
    feature: "Performance Improvement Plans (PIPs)",
    feature_menu_url: "/features/performance-improvement-plans",
    category: "qapi",
  },
  {
    label: "Incident Investigation",
    feature: "Incident Investigation",
    feature_menu_url: "/features/incident-investigation",
    category: "qapi",
  },
  {
    label: "Grievance Management",
    feature: "Grievance Management",
    feature_menu_url: "/features/grievance-management",
    category: "qapi",
  },
  {
    label: "Medical Director Engagement & Compliance",
    feature: "Medical Director Engagement & Compliance",
    feature_menu_url: "/features/medical-directorship-engagement-compliance",
    category: "qapi",
  },
  {
    label: "Action Plans",
    feature: "Action Plans",
    feature_menu_url: "/features/action-plans",
    category: "qapi",
  },
  {
    label: "Audit Management",
    feature: "Audit Management",
    feature_menu_url: "/features/audit-management",
    category: "qapi",
  },
  {
    label: "Facility Assessment",
    feature: "Facility Assessment",
    feature_menu_url: "/features/facility-assessment",
    category: "qapi",
  },
  {
    label: "Critical Elements Pathways",
    feature: "Critical Elements Pathways",
    feature_menu_url: "/features/critical-element-pathways",
    category: "qapi",
  },
  {
    label: "Mock Surveys",
    feature: "Mock Surveys",
    feature_menu_url: "/features/mock-surveys",
    category: "qapi",
  },
  {
    label: "Survey Management & Insights",
    feature: "Survey Management & Insights",
    feature_menu_url: "/features/survey-management",
    category: "qapi",
  },
  {
    label: "OSHA Respirator Protection Program",
    feature: "OSHA Respirator Protection Program",
    feature_menu_url: "/features/osha-respiratory-protection",
    category: "health",
  },
  {
    label: "Vaccination Tracking & Reportable Conditions",
    feature: "Vaccination Tracking & Reportable Conditions",
    feature_menu_url: "/features/vaccination-tracking-management",
    category: "health",
  },
  {
    label: "Metrics & Task Action Plans",
    feature: "Metrics & Task Action Plans",
    feature_menu_url: "/features/metric-task-action-plans",
    category: "operations",
  },
  {
    label: "Policy & Procedure Management",
    feature: "Policy & Procedure Management",
    feature_menu_url: "/features/policy-and-procedure-management",
    category: "operations",
  },
  {
    label: "Document Vault",
    feature: "Document Vault",
    feature_menu_url: "/features/document-vault",
    category: "operations",
  },
  {
    label: "Feedback & Satisfaction Surveys",
    feature: "Feedback & Satisfaction Surveys",
    feature_menu_url: "/features/feedback-and-satisfaction-surveys",
    category: "operations",
  },
  {
    label: "Emergency Preparedness",
    feature: "Emergency Preparedness",
    feature_menu_url: "/features/emergency-preparedness",
    category: "operations",
  },
];

export function getKeyFeaturePagesByCategory(
  category: KeyFeatureCategory,
): KeyFeaturePage[] {
  return KEY_FEATURE_PAGES.filter((page) => page.category === category);
}

/** Legacy public routes mapped to canonical `/features/...` URLs. */
const LEGACY_FEATURE_MENU_URL_ALIASES: Record<string, string> = {
  "/osha-respiratory-protection": "/features/osha-respiratory-protection",
};

function normalizePath(url: string): string {
  const trimmed = url.trim().toLowerCase();
  if (!trimmed) return "";
  return trimmed.replace(/\/+$/, "");
}

/** Normalize stored or legacy URLs to the canonical Key Feature path. */
export function resolveCanonicalFeatureMenuUrl(
  featureMenuUrl: string | null | undefined,
): string {
  const raw = (featureMenuUrl ?? "").trim();
  if (!raw) return "";

  const normalized = normalizePath(raw);
  const canonical = LEGACY_FEATURE_MENU_URL_ALIASES[normalized];
  if (canonical) return canonical;

  const page = KEY_FEATURE_PAGES.find(
    (entry) => normalizePath(entry.feature_menu_url) === normalized,
  );
  return page?.feature_menu_url ?? raw;
}

/** Resolve a readable feature label for list/detail views (never show internal menu IDs). */
export function resolveKeyFeatureLabel(
  feature: string | null | undefined,
  featureMenuUrl: string | null | undefined,
): string {
  const url = normalizePath(featureMenuUrl ?? "");
  if (url) {
    const byUrl = findKeyFeaturePageByMenuUrl(featureMenuUrl);
    if (byUrl) return byUrl.label;
  }

  const featureText = (feature ?? "").trim();
  if (featureText) {
    const byFeature = KEY_FEATURE_PAGES.find(
      (page) =>
        page.feature === featureText ||
        page.label === featureText ||
        normalizePath(page.feature_menu_url) === normalizePath(featureText),
    );
    if (byFeature) return byFeature.label;

    if (!/^\d+$/.test(featureText)) {
      return featureText;
    }
  }

  return "-";
}

export function findKeyFeaturePageByMenuUrl(
  featureMenuUrl: string | null | undefined,
): KeyFeaturePage | undefined {
  const url = normalizePath(featureMenuUrl ?? "");
  if (!url) return undefined;

  const canonical = LEGACY_FEATURE_MENU_URL_ALIASES[url] ?? url;
  return KEY_FEATURE_PAGES.find(
    (page) => normalizePath(page.feature_menu_url) === normalizePath(canonical),
  );
}
